// context/OrderContext.jsx
import React, { createContext, useContext, useState } from "react";
import { collection, addDoc, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../services/firebase";
import { useAuth } from "./AuthContext";
import { useCart } from "./CartContext";

const OrderContext = createContext();

export const useOrder = () => useContext(OrderContext);

export const OrderProvider = ({ children }) => {
    const [currentOrder, setCurrentOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const { currentUser } = useAuth();
    const { cart, getCartTotal, clearCart } = useCart();

    const createOrder = async (shippingData, paymentMethod, paymentIntentId = null) => {
        setLoading(true);
        setError("");

        try {
            const subtotal = getCartTotal();
            const shipping = 10.00;
            const tax = subtotal * 0.08;
            const total = subtotal + shipping + tax;

            const orderData = {
                userId: currentUser ? currentUser.uid : null,
                customerEmail: shippingData.email,
                items: cart,
                subtotal,
                shipping,
                tax,
                total,
                shippingAddress: shippingData,
                paymentMethod,
                paymentIntentId: paymentIntentId || null,
                status: paymentIntentId ? "paid" : "pending",
                createdAt: serverTimestamp(),
            };

            // Save to global orders collection
            const docRef = await addDoc(collection(db, "orders"), orderData);

            // Also mirror into the user's subcollection so Account page can read it
            if (currentUser) {
                await setDoc(
                    doc(db, "users", currentUser.uid, "orders", docRef.id),
                    orderData
                );
            }

            const orderWithId = { id: docRef.id, ...orderData };
            setCurrentOrder(orderWithId);
            clearCart();

            return orderWithId;
        } catch (err) {
            setError("Failed to create order: " + err.message);
            console.error("Error creating order:", err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const createPaymentIntent = async (amount) => {
        try {
            const response = await fetch('/.netlify/functions/create-payment-intent', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ amount: Math.round(amount * 100) }),
            });

            if (!response.ok) throw new Error('Network response was not ok');

            const { clientSecret } = await response.json();
            return clientSecret;
        } catch (err) {
            console.error('Error creating payment intent:', err);
            throw err;
        }
    };

    const value = { currentOrder, createOrder, createPaymentIntent, loading, error };

    return (
        <OrderContext.Provider value={value}>
            {children}
        </OrderContext.Provider>
    );
};

export default OrderContext;